import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import HeaderWithBack from '../../components/Header/headerWithBack';
import { useCssStyle } from '../../customHook/getCssStyle';
import { useTranslation } from 'react-i18next';
import InfoPopupModal from '../../components/infoPopupModal';
import { setGoHome, setShowLoginModal } from '../../Redux/Reducers/appConfigReducer';
import { getRoomControlData, sendCommand } from '../../Redux/Actions/roomControlAction';
import FetchingDataScreen from '../../components/fetchingDataScreen';
import { resetControl } from '../../Redux/Reducers/roomControlReducer';
import RoomTemperatureControl from './roomTemperature/RoomTemperatureControl';
import MasterControls from './masterControls';
import { setRenderFlashMessage } from '../../Redux/Actions/appConfigAction';
import CustomPressable from '../../components/customPressable/customPressable';
import { sendAnalytics } from '../../Redux/Actions/analyticsAction';
import { CLICKED, EXIT, INITIAL_LOADER_SCREEN } from '../../config/Constant';
import { calculateTimeDifference, getCleanUrl } from '../../utils/Helper';
import { ImageComp } from '../../components/Image/Image';
import { getDataLoadingScreen } from '../../js/getDataLoadingScreen';
import RoomControlSection from './roomControlSection';

  const RoomControl = () => {
    const dispatch = useDispatch();
    const getStyles = useCssStyle();
    const { t } = useTranslation();

    let widgetLaunchTime = new Date();

    const { isLoggedIn } = useSelector(state => state.appConfigSlice)
    const { currentWidget, webAppWidgetsInfo } = useSelector((state) => state.deviceConfigSlice);

    const { data, error, loading } = useSelector((state) => state.roomControlSlice);
    const isLutron = useSelector(
        (state) =>
          state.deviceConfigSlice?.webAppWidgetsInfo?.appConfigs
            ?.roomControlInterface === 'LUTRON'
      );

    const [initialLoder, setInitialLoader] = useState(true);
    const [showAcOverlay, setShowAcOverlay] = useState(false);

    const roomControlData = data?.data;

    // All rooms that have at least one HVAC unit
    const roomsWithHvac = roomControlData?.filter(
      (room) => Array.isArray(room.hvac) && room.hvac.length > 0
    ) || [];

    const allHvac = roomsWithHvac.flatMap((room) => room.hvac);

    const controlsToRender = React.useMemo(() => {
      const wt = currentWidget?.widgetType?.toLowerCase() || "ALL";

      if (wt.includes('multi-controller')) return ['ALL'];
      if (wt.includes('room temperature')) return ['hvac'];
      if (wt.includes('curtain')) return ['curtains'];
      if (wt.includes('lights')) return ['lights'];

      return ['ALL'];
    }, [currentWidget?.widgetType]);

    const canRender = (section = "") => {
      if (!section && controlsToRender.includes('ALL')) {
        return true;
      }

      return controlsToRender.includes(section);
    };

    // True when this widget is dedicated to temperature (not multi-controller)
    const isHvacOnlyMode = canRender('hvac') && !canRender('ALL');

    const [loaderState, setLoaderState] = useState({
      isVisible: false,
      message: ""
    });
    const [fetchApiError, setFetchApiError] = useState({
      errorStatus: false,
      errorMessage: '',
      onContinueFunc: null,
      primaryBtnLabel: '',
      onCloseFunc: null,
      secondaryBtnLabel: '',
    });

    const updateLoaderState = (state, msg) => {
      if (state) {
        setLoaderState({ isVisible: true, message: msg });
      } else {
        setLoaderState({ isVisible: false, message: "" });
      }
    }

    const updateFetchApiError = (
      errorStatus,
      errorMessage,
      onContinueFunc = null,
      primaryBtnLabel = '',
      onCloseFunc = null,
      secondaryBtnLabel = ''
    ) => {
      setFetchApiError({
        errorStatus: !!errorStatus,
        errorMessage: errorStatus ? (errorMessage || '') : '',
        onContinueFunc: errorStatus ? onContinueFunc || null : null,
        primaryBtnLabel: errorStatus ? primaryBtnLabel || '' : '',
        onCloseFunc: errorStatus ? onCloseFunc || null : null,
        secondaryBtnLabel: errorStatus ? secondaryBtnLabel || '' : '',
      });
    };

    const sendRoomCommand = (code, controlInterface, type) => {
      updateLoaderState(true, 'Loading...');

      dispatch(sendCommand(code, controlInterface, type, (status, message) => {
          updateLoaderState(false);
          dispatch(setRenderFlashMessage(true, { type: status ? 'success' : 'danger', message }));
        })
      );
    };

    useEffect(() => {
      if (!currentWidget?.displayName) return;
      dispatch(sendAnalytics(currentWidget.displayName, CLICKED, { "element": "widget", "pageName": "Room Controls" }));

      return () => {
        let widgetCloseTime = new Date
        let duration = calculateTimeDifference(widgetLaunchTime, widgetCloseTime, 'second');
        dispatch(sendAnalytics(currentWidget.displayName, EXIT, { "element": "widget", "pageName": "Room Controls", "duration": duration }));
      }

     // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWidget?.displayName])
    

    useEffect(() => {
      if (!isLoggedIn) {
        handleGoBack()
        dispatch(setShowLoginModal(true));
        return;
      } else {
        dispatch(resetControl())
        dispatch(getRoomControlData());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const handleGoBack = () => {
      dispatch(setGoHome(true));
    }

    useEffect(()=>{
      if(error && !loading) {
        updateFetchApiError(true, error, handleGoBack)
      } else {
        updateFetchApiError(false)
      }

      if(!loading){
        setInitialLoader(false)
      } else {
        setInitialLoader(true)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, loading])


    const renderHeader = () => {
      return (
        <View style={getStyles(['widgetmainContainerHeaderContainer', 'roomControlsHeaderContainer'])}>
          {
            currentWidget?.widgetHeaderImageUrl &&
            <ImageComp source={getCleanUrl(currentWidget?.widgetHeaderImageUrl)} style={getStyles(['widgetmainContainerHeaderImg', 'roomControlsHeaderImg'])} />
          }
          <View style={getStyles(['widgetmainContainerHeaderTitleContainer', 'roomControlsHeaderTitleContainer'])}>
            <Text style={getStyles(['widgetmainContainerHeaderTitle', 'roomControlsHeaderTitle'])}>{t('room_control.heading')}</Text>
            <Text style={getStyles(['widgetmainContainerHeaderSubTitle', 'roomControlsHeaderSubTitle'])}>{t('room_control.sub_heading')}</Text>
          </View>
        </View>
      )
    }

    const renderItem = (item) => {
      const curtains = item.curtain || [];
      const lights = item.lights || [];
      const label = item?.label;

      if(canRender("lights")) {
        return (
          <View style={getStyles(["roomControlsItemContainer"])} id='roomControlsItemContainer'>
            {label && <Text style={getStyles(["label", "roomControlsItemLabelText"])} id='label roomControlsItemLabelText'>{label}</Text>}
            <View style={getStyles(["roomControlsListItemMainContainer", "roomControlsLightsControls"])} id='roomControlsListItemMainContainer roomControlsLightsControls'>
              {lights?.length > 0 
                ? <RoomControlSection
                    // title={t('room_control.lights')}
                    groups={lights}
                    groupLabelKey="lightsSubTypeLabel"
                    itemsKey="lights"
                    codeKey="lightCode"
                    labelKey="lightLabel"
                    type="light"
                    controlInterface={isLutron ? { interface: 'lutron', action: 'setLightCtrlScene2' } : {}}
                    onAction={sendRoomCommand}
                    styleKey={"lights"}
                  />
              : <Text style={getStyles(["roomControlsNoControlsText"])}>No controls for this room</Text>}
            </View>
          </View>)
      }

      if(canRender("curtains")){
        return (
        <View style={getStyles(["roomControlsItemContainer"])} id='roomControlsItemContainer'>
            {label && <Text style={getStyles(["label", "roomControlsItemLabelText"])} id='label roomControlsItemLabelText'>{label}</Text>}
            <View style={getStyles(["roomControlsListItemMainContainer", "roomControlsCurtainsControls"])} id='roomControlsListItemMainContainer roomControlsCurtainsControls'>
              {curtains?.length > 0 
                ? <RoomControlSection
                    // title={t('room_control.curtain')}
                    groups={curtains}
                    groupLabelKey="curtainSubTypeLabel"
                    itemsKey="curtain"
                    codeKey="curtainCode"
                    labelKey="curtainLabel"
                    type="curtain"
                    controlInterface={isLutron ? { interface: 'lutron', action: 'setCurtain' } : {}}
                    onAction={sendRoomCommand}
                    styleKey={"curtains"}
                  />
                : <Text style={getStyles(["roomControlsNoControlsText"])}>No controls for this room</Text>
              }
          </View>
        </View>)
      }

      // ALL mode: render lights + curtains per room
      return (
        <View style={getStyles(["roomControlsItemContainer"])} id='roomControlsItemContainer'>
          {label && <Text style={getStyles(["label", "roomControlsItemLabelText"])} id='label roomControlsItemLabelText'>{label}</Text>}

          {lights?.length > 0 && (
            <View style={getStyles(["roomControlsListItemMainContainer", "roomControlsLightsControls"])} id='roomControlsListItemMainContainer roomControlsLightsControls'>
              <RoomControlSection
                // title={t('room_control.lights')}
                groups={lights}
                groupLabelKey="lightsSubTypeLabel"
                itemsKey="lights"
                codeKey="lightCode"
                labelKey="lightLabel"
                type="light"
                controlInterface={isLutron ? { interface: 'lutron', action: 'setLightCtrlScene2' } : {}}
                onAction={sendRoomCommand}
                styleKey={"lights"}

              />
            </View>
          )}

          {curtains?.length > 0 && (
            <View style={getStyles(["roomControlsListItemMainContainer", "roomControlsCurtainsControls"])} id='roomControlsListItemMainContainer roomControlsCurtainsControls'>
              <RoomControlSection
                // title={t('room_control.curtain')}
                groups={curtains}
                groupLabelKey="curtainSubTypeLabel"
                itemsKey="curtain"
                codeKey="curtainCode"
                labelKey="curtainLabel"
                type="curtain"
                controlInterface={isLutron ? { interface: 'lutron', action: 'setCurtain' } : {}}
                onAction={sendRoomCommand}
                styleKey={"curtains"}
              />
            </View>
          )}
        </View>
      );
    }

    return (
      <View style={getStyles(['widgetRootContainer', 'roomControlRootContainer'])}>
        <HeaderWithBack />

        {isHvacOnlyMode ? (
          <RoomTemperatureControl
            rooms={roomsWithHvac}
            configs={data?.configs}
            onAction={sendRoomCommand}
          />
        ) : (
          <ScrollView
            automaticallyAdjustKeyboardInsets={true}
            contentContainerStyle={getStyles(['widgetmainContainer', 'roomControlsMainContainer'])}
            showsVerticalScrollIndicator={false}
          >
            {renderHeader()}

          <View style={getStyles(['widgetmainContainerChildContainer', 'bookingInfoMainContainerChildContainer'])}>
              
              {canRender("ALL") && <MasterControls onAction={sendRoomCommand} />}

              {/* AC Controls button — only in ALL mode when HVAC units exist */}
              {/* {canRender("ALL") && allHvac.length > 0 && (
                <CustomPressable
                  style={getStyles(['roomControlAcControlsButton'])}
                  onPress={() => setShowAcOverlay(true)}
                >
                  <Text style={getStyles(['roomControlAcControlsButtonText'])}>
                    {'AC Controls'}
                  </Text>
                </CustomPressable>
              )} */}

              {/* AC Controls button — only in ALL mode when HVAC units exist */}
              {/* {canRender("ALL") && allHvac.length > 0 && (
                <CustomPressable
                  style={getStyles(['roomControlAcControlsButton'])}
                  onPress={() => setShowAcOverlay(true)}
                >
                  <Text style={getStyles(['roomControlAcControlsButtonText'])}>
                    {'AC Controls'}
                  </Text>
                </CustomPressable>
              )} */}

              {data?.data?.length ? 
                <View style={getStyles(["roomControlsListItemContainer", {flexDirection: "row", flexWrap: "wrap"}])} id='roomControlsListItemContainer'>
                  {data?.data?.map(renderItem)}
                </View>
                : <View style={getStyles(["roomControlsRetryContainer"])}>
                      <CustomPressable style={getStyles(["secondaryBtn", "roomControlRetryButtonContainer"])} onPress={() => {dispatch(getRoomControlData());}}>
                        <Text style={getStyles(["secondaryBtnTxt", "roomControlRetryButtonContainerTxt"])}>No controls present, retry?</Text>
                      </CustomPressable>
                  </View>}
            </View>
          </ScrollView>
        )}

        {/* ── AC Controls full-screen overlay (ALL / multi-controller mode) ── */}
        {showAcOverlay && (
          <View style={getStyles(['roomControlAcFullScreenOverlay'])}>
            <RoomTemperatureControl
              rooms={roomsWithHvac}
              configs={data?.configs}
              onAction={sendRoomCommand}
              onClose={() => setShowAcOverlay(false)}
            />
          </View>
        )}

        {fetchApiError.errorStatus && (
          <InfoPopupModal
            subtitle={fetchApiError.errorMessage}
            onContinue={fetchApiError.onContinueFunc ? fetchApiError.onContinueFunc : () => updateFetchApiError(false)}
            primaryBtnLabel={fetchApiError.primaryBtnLabel ? fetchApiError.primaryBtnLabel : 'Close'}
            onClose={fetchApiError.onCloseFunc ? fetchApiError.onCloseFunc : null}
            secondaryBtnLabel={fetchApiError.secondaryBtnLabel ? fetchApiError.secondaryBtnLabel : null}
          />
        )}

        {initialLoder && getDataLoadingScreen(INITIAL_LOADER_SCREEN)}
        {loaderState.isVisible && <FetchingDataScreen loaderMessage={loaderState.message} />}
      </View>
    );
  };

  export default RoomControl;
